import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  AccountTreeOutlined,
  AddOutlined,
  CheckCircleOutline,
  DeleteOutline,
  EditOutlined,
  ExpandLess,
  ExpandMore,
  GroupsOutlined,
  LocationCityOutlined,
  PersonOutline,
  PublishOutlined,
  DriveFileRenameOutline,
  SchoolOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";

import getCidades from "API/getCidades";
import { findAllAreasAtuacao } from "API/apiDadosComplementares";
import SearchSelect from "components/atoms/SearchSelect/SearchSelect";
import {
  atualizarCampoAdmin,
  atualizarPoloRegionalAdmin,
  atualizarRelacionamentoCampoAdmin,
  criarCampoAdmin,
  criarPoloRegionalAdmin,
  criarRelacionamentoCampoAdmin,
  criarVersaoRelacionamentosCamposAdmin,
  consultarPoloRegionalAdmin,
  desvincularMunicipiosPoloRegional,
  excluirCampoAdmin,
  excluirPoloRegionalAdmin,
  excluirRelacionamentoCampoAdmin,
  excluirVersaoRelacionamentosCamposAdmin,
  FiltroRelacionamentoCampo,
  listarCamposAdmin,
  listarColunasRelacionamentoCampoAdmin,
  listarPolosRegionaisAdmin,
  listarRelacionamentosCamposAdmin,
  listarVersoesRelacionamentosCamposAdmin,
  PoloRegionalAdmin,
  RelacionamentoCampoAdmin,
  RelacionamentoCampoPayload,
  RelacionamentoVersao,
  RegistroParametrizacao,
  vincularMunicipiosPoloRegional,
  atualizarVersaoRelacionamentosCamposAdmin,
  publicarVersaoRelacionamentosCamposAdmin,
} from "API/apiParametrizacaoCampos";
import ConfirmDialog from "components/atoms/ConfirmDialog/ConfirmDialog";
import MultiSelectGroupField from "components/atoms/MultiSelectField/MultiSelectGroupField";
import MultiSelectTransferField from "components/atoms/MultiSelectField/MultiSelectTransferField";
import { useAppFeedback } from "hooks/useAppFeedback";
import { useAuth } from "resources/AuthProvider";
import stylesButton from "styles/Button.module.css";
import stylesInput from "styles/Input.module.css";
import stylesTable from "styles/Table.module.css";
import styles from "styles/ParametrizacaoCampos.module.css";
import stylesGrid from "styles/Grid.module.css";
import stylesGeneral from "styles/General.module.css";

type BasePath = "dados_complementares" | "acoes";

type CampoConfig = {
  table: string;
  title: string;
  subtitle: string;
  basePath: BasePath;
  description?: boolean;
  fixedTipo?: "REGIONAL" | "VINCULADA";
  relation?: "superintendencia_id" | "grupo_id";
  relationLabel?: string;
};

type CampoForm = {
  id?: number;
  nome: string;
  descricao: string;
  ativo: boolean;
  relationId: string;
};

type Cidade = { id: number; nome: string };
type ProfessionalFieldOption = { value: string; label: string; table: string };

type DeleteTarget =
  | { type: "field"; config: CampoConfig; record: RegistroParametrizacao }
  | { type: "polo"; polo: PoloRegionalAdmin }
  | { type: "relationship"; relationship: RelacionamentoCampoAdmin }
  | { type: "relationshipVersion"; version: RelacionamentoVersao };

type RelationshipForm = {
  id?: number;
  sourceField: string;
  sourceValueId: string;
  applyToAll: boolean;
  targetField: string;
  targetLabel: string;
  optionsTable: string;
  optionsFilters: FiltroRelacionamentoCampo[];
  order: string;
  required: boolean;
  active: boolean;
};

type RelationshipPreviewValues = Record<string, number | number[] | null>;

const USER_FIELDS: CampoConfig[] = [
  { table: "orgaos_atuacao", title: "Órgão de Atuação (Municipal)", subtitle: "Órgão em que o usuário atua no âmbito municipal.", basePath: "dados_complementares" },
  { table: "lotacoes", title: "Lotação (Estadual)", subtitle: "Lotação do usuário no âmbito estadual.", basePath: "dados_complementares" },
  { table: "unidades_regionais", title: "Unidade CREAS Regional (Estadual)", subtitle: "Unidade CREAS Regional de atuação.", basePath: "dados_complementares" },
  { table: "diretorias", title: "Diretoria Regional (Estadual)", subtitle: "Diretoria Regional de atuação.", basePath: "dados_complementares", fixedTipo: "REGIONAL" },
  { table: "superintendencias", title: "Superintendência (Estadual)", subtitle: "Superintendência de atuação.", basePath: "dados_complementares" },
  { table: "diretorias", title: "Diretoria Vinculada (Estadual)", subtitle: "Diretoria vinculada de atuação.", basePath: "dados_complementares", fixedTipo: "VINCULADA", relation: "superintendencia_id", relationLabel: "Superintendência" },
  { table: "instituicoes_estaduais", title: "Instituições", subtitle: "Instituições estaduais e municipais.", basePath: "dados_complementares" },
  { table: "grupos_areas", title: "Grupos de Atuação", subtitle: "Grupos de atuação vinculados às áreas de atuação.", basePath: "dados_complementares" },
  { table: "areas_atuacao", title: "Área de Atuação", subtitle: "Área de atuação vinculada aos dados profissionais.", basePath: "dados_complementares", relation: "grupo_id", relationLabel: "Grupo de atuação" },
  { table: "funcoes_desempenhadas", title: "Funções Desempenhadas", subtitle: "Funções atreladas aos dados profissionais.", basePath: "dados_complementares" },
  { table: "vinculos_empregaticios", title: "Vínculos Empregatícios", subtitle: "Vínculos empregatícios atrelados aos dados profissionais.", basePath: "dados_complementares" },
];

const ACTION_FIELDS: CampoConfig[] = [
  { table: "temas", title: "Tema(s) Abordado(s)", subtitle: "Gerencie os campos das ações de qualificação.", basePath: "acoes", description: true },
  { table: "tipologias", title: "Tipologia(s)", subtitle: "Gerencie as tipologias das ações.", basePath: "acoes", description: true },
];

const PROFESSIONAL_TABLES = Array.from(new Set([
  ...USER_FIELDS.map((field) => field.table),
  "municipios",
]));

const PROFESSIONAL_TABLE_LABELS: Record<string, string> = {
  municipios: "Municípios",
};

const getProfessionalTableLabel = (table: string) => (
  PROFESSIONAL_TABLE_LABELS[table]
  || USER_FIELDS.find((field) => field.table === table)?.title
  || table
);

const PROFESSIONAL_FIELD_OPTIONS: ProfessionalFieldOption[] = [
  { value: "ambito_id", label: "Âmbito", table: "ambitos" },
  { value: "municipio_id", label: "Município", table: "municipios" },
  { value: "areas_atuacao", label: "Área de Atuação", table: "areas_atuacao" },
  { value: "lotacao_id", label: "Lotação", table: "lotacoes" },
  { value: "vinculo_id", label: "Vínculo Empregatício", table: "vinculos_empregaticios" },
  { value: "funcao_id", label: "Função Desempenhada", table: "funcoes_desempenhadas" },
  { value: "superintendencia_id", label: "Superintendência", table: "superintendencias" },
  { value: "unidade_id", label: "Unidades Regionais", table: "unidades_regionais" },
  { value: "diretoria_regional_id", label: "Diretoria Regional", table: "diretorias" },
  { value: "diretoria_interna_id", label: "Diretoria Vinculada", table: "diretorias" },
  { value: "orgao_atuacao_id", label: "Órgão de Atuação", table: "orgaos_atuacao" },
  { value: "instituicao_id", label: "Instituições Estaduais", table: "instituicoes_estaduais" },
];

const SOURCE_FIELD_TABLE_MAP = Object.fromEntries(
  PROFESSIONAL_FIELD_OPTIONS.map((field) => [field.value, field.table]),
) as Record<string, string>;

const fieldToLabel = (field: string) => field
  .replace(/_id$/i, "")
  .replace(/_/g, " ")
  .replace(/\b\w/g, (character) => character.toUpperCase());

const toSearchSelectOptions = (values: Array<{ value: string; label: string }>) => values.map((item, index) => ({
  id: index + 1,
  nome: item.label,
  value: item.value,
}));

const getSearchSelectValue = (options: Array<{ id: number; value: string }>, value: string) => (
  options.find((option) => option.value === value)?.id ?? null
);

const EMPTY_FIELD_FORM: CampoForm = { nome: "", descricao: "", ativo: true, relationId: "" };
const EMPTY_RELATIONSHIP_FORM: RelationshipForm = {
  sourceField: "",
  sourceValueId: "",
  applyToAll: false,
  targetField: "",
  targetLabel: "",
  optionsTable: "",
  optionsFilters: [],
  order: "",
  required: false,
  active: true,
};

const isActive = (value: unknown) => value === true || value === 1 || value === "1";

const getPreviewRelationshipDependentFields = (rules: RelacionamentoCampoAdmin[], sourceField: string) => {
  const dependentFields = new Set<string>();
  const pendingFields = [sourceField];

  while (pendingFields.length) {
    const currentField = pendingFields.shift();
    if (!currentField) continue;

    rules
      .filter((rule) => isActive(rule.ativo) && rule.source_field === currentField)
      .forEach((rule) => {
        if (rule.target_field === sourceField || dependentFields.has(rule.target_field)) return;
        dependentFields.add(rule.target_field);
        pendingFields.push(rule.target_field);
      });
  }

  return dependentFields;
};

const PREVIEW_AMBITOS: RegistroParametrizacao[] = [
  { id: 1, nome: "Estadual", ativo: true },
  { id: 2, nome: "Municipal", ativo: true },
];

type RelationshipFormPreviewProps = {
  rules: RelacionamentoCampoAdmin[];
  records: Record<string, RegistroParametrizacao[]>;
  cities: Cidade[];
  areasAtuacao: RegistroParametrizacao[];
};

const RelationshipFormPreview = ({ rules, records, cities, areasAtuacao }: RelationshipFormPreviewProps) => {
  const [values, setValues] = useState<RelationshipPreviewValues>({ ambito_id: null });

  const getValue = (field: string) => values[field] ?? null;

  const getOptions = (table: string): RegistroParametrizacao[] => {
    if (table === "ambitos") return PREVIEW_AMBITOS;
    if (table === "municipios") return cities;
    if (table === "areas_atuacao") return areasAtuacao;
    return records[table] || [];
  };

  const getFilteredOptions = (rule: RelacionamentoCampoAdmin) => (
    getOptions(rule.options_table).filter((option) => (rule.options_filters || []).every((filter) => {
      const expectedValue = filter.source_field ? getValue(filter.source_field) : filter.value;
      const optionValue = option[filter.field];
      const equals = Array.isArray(expectedValue)
        ? expectedValue.map(String).includes(String(optionValue ?? ""))
        : String(optionValue ?? "") === String(expectedValue ?? "");
      return filter.operator === "!=" ? !equals : equals;
    }))
  );

  const targetRules = useMemo(() => {
    const visibleRules = rules
      .filter((rule) => isActive(rule.ativo))
      .filter((rule) => {
        const sourceValue = getValue(rule.source_field);
        const hasSourceValue = Array.isArray(sourceValue) ? sourceValue.length > 0 : sourceValue !== null;

        if (!hasSourceValue) return false;
        if (isActive(rule.aplicar_para_todos) || rule.source_value_id === null) return true;

        return Array.isArray(sourceValue)
          ? sourceValue.includes(Number(rule.source_value_id))
          : Number(sourceValue) === Number(rule.source_value_id);
      })
      .sort((first, second) => (first.ordem ?? 0) - (second.ordem ?? 0));

    const displayedTargets = new Set<string>();
    return visibleRules.filter((rule) => {
      if (rule.target_field === "ambito_id" || displayedTargets.has(rule.target_field)) return false;
      displayedTargets.add(rule.target_field);
      return true;
    });
  }, [rules, values]);

  const handleValueChange = (field: string, value: RelationshipPreviewValues[string]) => {
    const dependentFields = getPreviewRelationshipDependentFields(rules, field);
    setValues((current) => {
      const nextValues = { ...current, [field]: value };
      dependentFields.forEach((dependentField) => {
        nextValues[dependentField] = dependentField === "areas_atuacao" ? [] : null;
      });
      return nextValues;
    });
  };

  return (
    <div className={styles.previewForm}>
      <div className={styles.previewHint}>Selecione valores para simular os campos exibidos pelas regras desta versão.</div>
      <div className={`${stylesGrid.grid12} ${styles.previewFields}`}>
        <div className={`${styles.filterField} ${stylesGrid.inputGroup6}`}>
          <label>Âmbito de Atuação *</label>
          <SearchSelect
            label=""
            placeholder="Selecione"
            options={PREVIEW_AMBITOS}
            value={typeof values.ambito_id === "number" ? values.ambito_id : null}
            onChange={(value) => handleValueChange("ambito_id", value)}
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            labelClassname={stylesInput.labelSelect}
            inputClassname={stylesInput.inputSoft}
          />
        </div>

        {targetRules.map((rule) => {
          const required = isActive(rule.obrigatorio);
          const label = `${rule.target_field === "municipio_id" ? "Município de Atuação" : rule.target_label}${required ? " *" : ""}`;
          const options = getFilteredOptions(rule);

          if (rule.target_field === "areas_atuacao") {
            const selectedIds = Array.isArray(values.areas_atuacao) ? values.areas_atuacao : [];
            const groupedOptions = options.map((option) => ({
              group: String(option.grupo_nome || ""),
              name: option.nome,
            }));
            const selectedValues = options
              .filter((option) => selectedIds.includes(option.id))
              .map((option) => ({
                group: String(option.grupo_nome || ""),
                name: option.nome,
              }));

            return (
              <div key={rule.id} className={`${styles.filterField} ${stylesGrid.inputGroupFull}`}>
                <label>{label}</label>
                <MultiSelectGroupField
                  label=""
                  value={selectedValues}
                  onChange={(selected) => {
                    const selectedIdsByOption = selected
                      .map((item) => options.find((option) => (
                        option.nome === item.name && String(option.grupo_nome || "") === item.group
                      ))?.id)
                      .filter((id): id is number => typeof id === "number");
                    handleValueChange("areas_atuacao", selectedIdsByOption);
                  }}
                  options={groupedOptions}
                  fullWidth
                  required={required}
                  maxSelections={4}
                />
              </div>
            );
          }

          return (
            <div key={rule.id} className={`${styles.filterField} ${stylesGrid.inputGroup6}`}>
              <label>{label}</label>
              <SearchSelect
                label=""
                placeholder="Selecione"
                options={options}
                value={typeof getValue(rule.target_field) === "number" ? getValue(rule.target_field) as number : null}
                onChange={(value) => handleValueChange(rule.target_field, value)}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSoft}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ParametrizacaoCampos = () => {
  const { hasActionPermission } = useAuth();
  const { showError, showSuccess } = useAppFeedback();
  const canViewUsers = hasActionPermission("cadastros_usuarios.view_all");
  const canCreateUsers = hasActionPermission("cadastros_usuarios.create");
  const canUpdateUsers = hasActionPermission("cadastros_usuarios.update_all");
  const canDeleteUsers = hasActionPermission("cadastros_usuarios.delete_all");
  const canViewActions = hasActionPermission("cadastros_acoes.view_all");
  const canCreateActions = hasActionPermission("cadastros_acoes.create");
  const canUpdateActions = hasActionPermission("cadastros_acoes.update_all");
  const canDeleteActions = hasActionPermission("cadastros_acoes.delete_all");
  const canViewRegionals = hasActionPermission("regionais.view_all");
  const canCreateRegionals = hasActionPermission("regionais.create");
  const canUpdateRegionals = hasActionPermission("regionais.update_all");
  const canDeleteRegionals = hasActionPermission("regionais.delete_all");

  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    users: true,
    actions: true,
    regionals: true,
    relationships: true,
  });
  const [expandedFields, setExpandedFields] = useState<Record<string, boolean>>({});
  const [records, setRecords] = useState<Record<string, RegistroParametrizacao[]>>({});
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [polos, setPolos] = useState<PoloRegionalAdmin[]>([]);
  const [loadingPolos, setLoadingPolos] = useState(false);
  const [cities, setCities] = useState<Cidade[]>([]);
  const [loadingSourceCities, setLoadingSourceCities] = useState(false);
  const [areasAtuacao, setAreasAtuacao] = useState<RegistroParametrizacao[]>([]);
  const [loadingAreasAtuacao, setLoadingAreasAtuacao] = useState(false);
  const [relationships, setRelationships] = useState<RelacionamentoCampoAdmin[]>([]);
  const [relationshipVersions, setRelationshipVersions] = useState<RelacionamentoVersao[]>([]);
  const [selectedRelationshipVersionId, setSelectedRelationshipVersionId] = useState<number | null>(null);
  const [loadingRelationshipVersions, setLoadingRelationshipVersions] = useState(false);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [relationshipPreviewOpen, setRelationshipPreviewOpen] = useState(false);
  const [versionName, setVersionName] = useState("");
  const [editingVersion, setEditingVersion] = useState<RelacionamentoVersao | null>(null);
  const [tableColumns, setTableColumns] = useState<Record<string, string[]>>({});
  const [loadingRelationships, setLoadingRelationships] = useState(false);
  const [relationshipDialogOpen, setRelationshipDialogOpen] = useState(false);
  const [relationshipForm, setRelationshipForm] = useState<RelationshipForm>(EMPTY_RELATIONSHIP_FORM);
  const [fieldConfig, setFieldConfig] = useState<CampoConfig | null>(null);
  const [fieldForm, setFieldForm] = useState<CampoForm>(EMPTY_FIELD_FORM);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [poloDialogOpen, setPoloDialogOpen] = useState(false);
  const [poloForm, setPoloForm] = useState({ id: 0, nome: "", cidade_sede: "", ativo: true, motivo_alteracao: "" });
  const [municipiosPolo, setMunicipiosPolo] = useState<PoloRegionalAdmin | null>(null);
  const [selectedCityIds, setSelectedCityIds] = useState<number[]>([]);
  const [loadingMunicipiosPolo, setLoadingMunicipiosPolo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const selectedRelationshipVersion = useMemo(
    () => relationshipVersions.find((version) => version.id === selectedRelationshipVersionId) || null,
    [relationshipVersions, selectedRelationshipVersionId],
  );
  const isRelationshipDraft = selectedRelationshipVersion?.status === "RASCUNHO";

  const relatedOptions = useMemo(() => ({
    superintendencia_id: records.superintendencias || [],
    grupo_id: records.grupos_areas || [],
  }), [records]);

  const sourceValues = useMemo(() => {
    if (relationshipForm.sourceField === "municipio_id") return cities;
    if (relationshipForm.sourceField === "areas_atuacao") return areasAtuacao;

    const sourceTable = SOURCE_FIELD_TABLE_MAP[relationshipForm.sourceField];
    return sourceTable ? records[sourceTable] || [] : [];
  }, [areasAtuacao, cities, records, relationshipForm.sourceField]);

  const sourceFieldConfig = useMemo<CampoConfig | undefined>(() => {
    const sourceField = PROFESSIONAL_FIELD_OPTIONS.find((field) => field.value === relationshipForm.sourceField);
    return sourceField
      ? {
        table: sourceField.table,
        title: sourceField.label,
        subtitle: "",
        basePath: "dados_complementares",
      }
      : undefined;
  }, [relationshipForm.sourceField]);

  const sourceValuesLoading = Boolean(
    relationshipForm.sourceField === "municipio_id"
      ? loadingSourceCities
      : relationshipForm.sourceField === "areas_atuacao"
        ? loadingAreasAtuacao
      : sourceFieldConfig
        && loadingFields[`${sourceFieldConfig.table}:${sourceFieldConfig.fixedTipo || "all"}`],
  );

  const loadField = useCallback(async (config: CampoConfig) => {
    const cacheKey = `${config.table}:${config.fixedTipo || "all"}`;
    setLoadingFields((current) => ({ ...current, [cacheKey]: true }));
    try {
      const response = await listarCamposAdmin(config.basePath, config.table);
      const filtered = config.fixedTipo ? response.filter((item) => item.tipo === config.fixedTipo) : response;
      setRecords((current) => ({ ...current, [cacheKey]: filtered, [config.table]: response }));
    } catch {
      setMessage({ type: "error", text: `Não foi possível carregar ${config.title}.` });
    } finally {
      setLoadingFields((current) => ({ ...current, [cacheKey]: false }));
    }
  }, []);

  const loadPolos = useCallback(async () => {
    setLoadingPolos(true);
    try {
      setPolos(await listarPolosRegionaisAdmin());
    } catch {
      setMessage({ type: "error", text: "Não foi possível carregar os polos regionais." });
    } finally {
      setLoadingPolos(false);
    }
  }, []);

  const loadRelationships = useCallback(async (versionId: number | null) => {
    if (!versionId) {
      setRelationships([]);
      return;
    }
    setLoadingRelationships(true);
    try {
      setRelationships(await listarRelacionamentosCamposAdmin(versionId));
    } catch (error) {
      showError("Não foi possível carregar os relacionamentos entre campos.", error);
    } finally {
      setLoadingRelationships(false);
    }
  }, [showError]);

  const loadRelationshipVersions = useCallback(async (preferredVersionId?: number | null) => {
    setLoadingRelationshipVersions(true);
    try {
      const versions = await listarVersoesRelacionamentosCamposAdmin({ categoria: "professional_form" });
      setRelationshipVersions(versions);
      const selectedId = preferredVersionId
        ?? selectedRelationshipVersionId
        ?? versions.find((version) => version.status === "RASCUNHO")?.id
        ?? versions.find((version) => version.status === "PUBLICADA")?.id
        ?? versions[0]?.id
        ?? null;
      setSelectedRelationshipVersionId(selectedId);
      await loadRelationships(selectedId);
    } catch (error) {
      showError("Não foi possível carregar as versões de relacionamento.", error);
    } finally {
      setLoadingRelationshipVersions(false);
    }
  }, [loadRelationships, selectedRelationshipVersionId, showError]);

  const loadRelationshipColumns = useCallback(async (table: string) => {
    const columns = await listarColunasRelacionamentoCampoAdmin(table);
    setTableColumns((current) => ({ ...current, [table]: columns }));
    return columns;
  }, []);

  const loadCities = useCallback(async () => {
    setLoadingSourceCities(true);
    try {
      const loadedCities = await getCidades();
      const normalizedCities = (Array.isArray(loadedCities) ? loadedCities : [])
        .map((city) => ({ id: Number(city.id ?? city.codigo), nome: String(city.nome ?? city.name ?? "") }))
        .filter((city) => city.id && city.nome)
        .sort((first, second) => first.nome.localeCompare(second.nome));
      setCities(normalizedCities);
    } catch {
      setCities([]);
    } finally {
      setLoadingSourceCities(false);
    }
  }, []);

  const loadAreasAtuacao = useCallback(async () => {
    setLoadingAreasAtuacao(true);
    try {
      const response = await findAllAreasAtuacao();
      setAreasAtuacao((Array.isArray(response) ? response : []).map((area) => ({
        id: area.id,
        nome: area.nome,
        grupo_id: area.grupo_id,
        grupo_nome: area.grupo_nome,
        ativo: true,
      })));
    } catch {
      setAreasAtuacao([]);
    } finally {
      setLoadingAreasAtuacao(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    const loadPageData = async () => {
      setInitialLoading(true);
      const tasks: Promise<unknown>[] = [loadCities()];
      if (canViewUsers) tasks.push(...USER_FIELDS.map(loadField), loadRelationshipVersions(), loadAreasAtuacao());
      if (canViewActions) tasks.push(...ACTION_FIELDS.map(loadField));
      if (canViewRegionals) tasks.push(loadPolos());

      await Promise.all(tasks);
      if (active) setInitialLoading(false);
    };

    void loadPageData();

    return () => {
      active = false;
    };
  }, [canViewActions, canViewRegionals, canViewUsers, loadAreasAtuacao, loadCities, loadField, loadPolos, loadRelationshipVersions]);

  useEffect(() => {
    if (relationshipDialogOpen && relationshipForm.optionsTable) {
      void loadRelationshipColumns(relationshipForm.optionsTable).catch((error) => {
        showError("Não foi possível carregar as colunas da tabela selecionada.", error);
      });
    }
  }, [loadRelationshipColumns, relationshipDialogOpen, relationshipForm.optionsTable, showError]);

  useEffect(() => {
    if (!relationshipDialogOpen || !sourceFieldConfig || ["municipio_id", "areas_atuacao"].includes(relationshipForm.sourceField)) return;

    void loadField(sourceFieldConfig);
  }, [loadField, relationshipDialogOpen, relationshipForm.sourceField, sourceFieldConfig]);

  const handleRelationshipSourceFieldChange = (sourceField: string) => {
    setRelationshipForm((current) => ({ ...current, sourceField, sourceValueId: "" }));

    if (sourceField === "municipio_id") {
      void loadCities();
      return;
    }

    const sourceOption = PROFESSIONAL_FIELD_OPTIONS.find((field) => field.value === sourceField);
    if (sourceOption) {
      void loadField({
        table: sourceOption.table,
        title: sourceOption.label,
        subtitle: "",
        basePath: "dados_complementares",
      });
    }
  };

  const openFieldDialog = (config: CampoConfig, record?: RegistroParametrizacao) => {
    setFieldConfig(config);
    setFieldForm({
      id: record?.id,
      nome: record?.nome || "",
      descricao: String(record?.descricao || ""),
      ativo: record ? isActive(record.ativo) : true,
      relationId: String(record?.[config.relation || ""] || ""),
    });
    setFieldDialogOpen(true);
  };

  const saveField = async () => {
    if (!fieldConfig || !fieldForm.nome.trim()) {
      setMessage({ type: "error", text: "Informe o nome do registro." });
      return;
    }

    const payload: Record<string, unknown> = { nome: fieldForm.nome.trim(), ativo: fieldForm.ativo ? 1 : 0 };
    if (fieldConfig.description) payload.descricao = fieldForm.descricao || null;
    if (fieldConfig.fixedTipo) payload.tipo = fieldConfig.fixedTipo;
    if (fieldConfig.relation) {
      if (!fieldForm.relationId) {
        setMessage({ type: "error", text: `Selecione ${fieldConfig.relationLabel?.toLowerCase()}.` });
        return;
      }
      payload[fieldConfig.relation] = Number(fieldForm.relationId);
    }

    setSaving(true);
    try {
      if (fieldForm.id) {
        await atualizarCampoAdmin(fieldConfig.basePath, fieldConfig.table, fieldForm.id, payload);
      } else {
        await criarCampoAdmin(fieldConfig.basePath, fieldConfig.table, payload);
      }
      await loadField(fieldConfig);
      setFieldDialogOpen(false);
      setMessage({ type: "success", text: "Registro salvo com sucesso." });
    } catch {
      setMessage({ type: "error", text: "Não foi possível salvar o registro." });
    } finally {
      setSaving(false);
    }
  };

  const toggleFieldStatus = async (config: CampoConfig, record: RegistroParametrizacao) => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = { ...record, nome: record.nome, ativo: isActive(record.ativo) ? 0 : 1 };
      if (config.fixedTipo) payload.tipo = config.fixedTipo;
      await atualizarCampoAdmin(config.basePath, config.table, record.id, payload);
      await loadField(config);
    } catch {
      setMessage({ type: "error", text: "Não foi possível alterar o status." });
    } finally {
      setSaving(false);
    }
  };

  const openPoloDialog = (polo?: PoloRegionalAdmin) => {
    setPoloForm({
      id: polo?.id || 0,
      nome: polo?.nome || "",
      cidade_sede: polo?.cidade_sede || "",
      ativo: polo ? isActive(polo.ativo) : true,
      motivo_alteracao: "",
    });
    setPoloDialogOpen(true);
  };

  const savePolo = async () => {
    if (!poloForm.nome.trim() || !poloForm.cidade_sede.trim()) {
      setMessage({ type: "error", text: "Informe o nome e a cidade sede do polo." });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...poloForm, id: undefined, ativo: poloForm.ativo ? 1 : 0 };
      if (poloForm.id) await atualizarPoloRegionalAdmin(poloForm.id, payload);
      else await criarPoloRegionalAdmin(payload);
      await loadPolos();
      setPoloDialogOpen(false);
      setMessage({ type: "success", text: "Polo regional salvo com sucesso." });
    } catch {
      setMessage({ type: "error", text: "Não foi possível salvar o polo regional." });
    } finally {
      setSaving(false);
    }
  };

  const openMunicipiosDialog = async (polo: PoloRegionalAdmin) => {
    setMunicipiosPolo(polo);
    setSelectedCityIds([]);
    setLoadingMunicipiosPolo(true);

    try {
      const poloDetalhado = await consultarPoloRegionalAdmin(polo.id);
      const selected = [...(poloDetalhado.municipios || []), ...(poloDetalhado.cidades || [])]
        .map((city) => Number(city.city_id ?? city.id))
        .filter(Boolean);

      setMunicipiosPolo(poloDetalhado);
      setSelectedCityIds([...new Set(selected)]);
    } catch (error) {
      setMunicipiosPolo(null);
      showError("Não foi possível carregar os municípios deste polo regional.", error);
    } finally {
      setLoadingMunicipiosPolo(false);
    }
  };

  const saveMunicipios = async () => {
    if (!municipiosPolo) return;
    const currentIds = [...(municipiosPolo.municipios || []), ...(municipiosPolo.cidades || [])]
      .map((city) => Number(city.city_id ?? city.id))
      .filter(Boolean);
    const toAdd = selectedCityIds.filter((id) => !currentIds.includes(id));
    const toRemove = currentIds.filter((id) => !selectedCityIds.includes(id));

    setSaving(true);
    try {
      if (toAdd.length) await vincularMunicipiosPoloRegional(municipiosPolo.id, toAdd);
      if (toRemove.length) await desvincularMunicipiosPoloRegional(municipiosPolo.id, toRemove);
      await loadPolos();
      setMunicipiosPolo(null);
      setMessage({ type: "success", text: "Municípios atualizados com sucesso." });
    } catch {
      setMessage({ type: "error", text: "Não foi possível atualizar os municípios do polo." });
    } finally {
      setSaving(false);
    }
  };

  const openRelationshipDialog = (relationship?: RelacionamentoCampoAdmin) => {
    setRelationshipForm({
      id: relationship?.id,
      sourceField: relationship?.source_field || "",
      sourceValueId: relationship?.source_value_id ? String(relationship.source_value_id) : "",
      applyToAll: isActive(relationship?.aplicar_para_todos) || relationship?.source_value_id === null,
      targetField: relationship?.target_field || "",
      targetLabel: relationship?.target_label || "",
      optionsTable: relationship?.options_table || "",
      optionsFilters: relationship?.options_filters || [],
      order: relationship?.ordem ? String(relationship.ordem) : "",
      required: isActive(relationship?.obrigatorio),
      active: relationship ? isActive(relationship.ativo) : true,
    });
    setRelationshipDialogOpen(true);
  };

  const saveRelationship = async () => {
    if (!selectedRelationshipVersionId || !isRelationshipDraft) {
      showError("Selecione uma versão em rascunho para alterar as regras.");
      return;
    }

    if (!relationshipForm.sourceField || (!relationshipForm.applyToAll && !relationshipForm.sourceValueId) || !relationshipForm.targetField || !relationshipForm.targetLabel || !relationshipForm.optionsTable) {
      showError("Preencha os campos obrigatórios do relacionamento.");
      return;
    }

    const payload: RelacionamentoCampoPayload = {
      versao_id: selectedRelationshipVersionId || 0,
      source_field: relationshipForm.sourceField,
      ...(relationshipForm.applyToAll ? {} : { source_value_id: Number(relationshipForm.sourceValueId) }),
      aplicar_para_todos: relationshipForm.applyToAll,
      target_field: relationshipForm.targetField,
      target_label: relationshipForm.targetLabel,
      options_table: relationshipForm.optionsTable,
      options_filters: relationshipForm.optionsFilters.filter((filter) => (
        filter.field
        && filter.operator
        && (filter.source_field || String(filter.value ?? "").trim())
      )),
      ordem: relationshipForm.order ? Number(relationshipForm.order) : null,
      obrigatorio: relationshipForm.required,
      ativo: relationshipForm.active,
    };

    setSaving(true);
    try {
      if (relationshipForm.id) await atualizarRelacionamentoCampoAdmin(relationshipForm.id, payload);
      else await criarRelacionamentoCampoAdmin(payload);
      await loadRelationships(selectedRelationshipVersionId);
      setRelationshipDialogOpen(false);
      showSuccess("Relacionamento salvo com sucesso.");
    } catch (error) {
      showError("Não foi possível salvar o relacionamento entre campos.", error);
    } finally {
      setSaving(false);
    }
  };

  const openVersionDialog = (version?: RelacionamentoVersao) => {
    setEditingVersion(version || null);
    setVersionName(version?.nome || "");
    setVersionDialogOpen(true);
  };

  const saveRelationshipVersion = async () => {
    if (editingVersion && editingVersion.status !== "RASCUNHO") return;

    setSaving(true);
    try {
      if (editingVersion) {
        await atualizarVersaoRelacionamentosCamposAdmin(editingVersion.id, { nome: versionName });
        await loadRelationshipVersions(editingVersion.id);
      } else {
        const response = await criarVersaoRelacionamentosCamposAdmin({
          categoria: "professional_form",
          nome: versionName || undefined,
          copiar_de_versao_id: selectedRelationshipVersionId || undefined,
        });
        const createdVersionId = (response as { data?: RelacionamentoVersao }).data?.id;
        await loadRelationshipVersions(createdVersionId);
      }
      setVersionDialogOpen(false);
      showSuccess("Versão salva com sucesso.");
    } catch (error) {
      showError("Não foi possível salvar a versão.", error);
    } finally {
      setSaving(false);
    }
  };

  const publishRelationshipVersion = async () => {
    if (!selectedRelationshipVersion || selectedRelationshipVersion.status !== "RASCUNHO") return;

    setSaving(true);
    try {
      await publicarVersaoRelacionamentosCamposAdmin(selectedRelationshipVersion.id);
      await loadRelationshipVersions(selectedRelationshipVersion.id);
      showSuccess("Versão publicada com sucesso.");
    } catch (error) {
      showError("Não foi possível publicar a versão.", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      if (deleteTarget.type === "field") {
        const { config, record } = deleteTarget;
        await excluirCampoAdmin(config.basePath, config.table, record.id);
        await loadField(config);
      } else if (deleteTarget.type === "polo") {
        await excluirPoloRegionalAdmin(deleteTarget.polo.id);
        await loadPolos();
      } else if (deleteTarget.type === "relationshipVersion") {
        await excluirVersaoRelacionamentosCamposAdmin(deleteTarget.version.id);
        await loadRelationshipVersions();
      } else {
        await excluirRelacionamentoCampoAdmin(deleteTarget.relationship.id);
        await loadRelationships(selectedRelationshipVersionId);
      }

      setDeleteTarget(null);
      showSuccess("Registro excluído com sucesso.");
    } catch (error) {
      showError("Não foi possível excluir o registro.", error);
    } finally {
      setDeleting(false);
    }
  };

  const _renderField = (config: CampoConfig, canCreate: boolean, canUpdate: boolean, canDelete: boolean) => {
    const key = `${config.table}:${config.fixedTipo || "all"}`;
    const data = records[key] || [];
    const relationRecords = config.relation ? relatedOptions[config.relation] : [];

    return (
      <div className={styles.fieldCard} key={key}>
        <div className={styles.fieldHeader}>
          <div>
            <div className={styles.fieldTitle}>{config.title}</div>
            <div className={styles.fieldDescription}>{config.subtitle}</div>
          </div>
          {canCreate && (
            <Button className={stylesButton.buttonSmallSoftPrimary} startIcon={<AddOutlined />} onClick={() => openFieldDialog(config)}>
              Novo registro
            </Button>
          )}
        </div>
        <div className={styles.tableWrap}>
          <Table size="small">
            <TableHead className={stylesTable.tableHead}>
              <TableRow>
                <TableCell className={stylesTable.tableHeadRow}>Nome</TableCell>
                {config.relation && <TableCell className={stylesTable.tableHeadRow}>{config.relationLabel}</TableCell>}
                <TableCell align="center" className={stylesTable.tableHeadRow}>Status</TableCell>
                {(canUpdate || canDelete) && <TableCell align="center" className={stylesTable.tableHeadRow}>Ações</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {initialLoading || loadingFields[key] ? (
                Array.from({ length: 4 }).map((_, index) => <TableRow key={`field-skeleton-${index}`}><TableCell colSpan={(canUpdate || canDelete) ? (config.relation ? 4 : 3) : (config.relation ? 3 : 2)}><Skeleton animation="wave" height={26} /></TableCell></TableRow>)
              ) : data.length ? data.map((record, index) => {
                const relationName = relationRecords.find((item) => item.id === Number(record[config.relation || ""]))?.nome || "-";
                return (
                  <TableRow key={record.id} className={`${stylesTable.tableRow} ${index % 2 === 0 ? stylesTable.tableRowEven : stylesTable.tableRowOdd}`}>
                    <TableCell className={stylesTable.textXs}>{record.nome}</TableCell>
                    {config.relation && <TableCell className={stylesTable.textXs}>{relationName}</TableCell>}
                    <TableCell align="center"><span className={isActive(record.ativo) ? styles.statusActive : styles.statusInactive}>{isActive(record.ativo) ? "Ativo" : "Inativo"}</span></TableCell>
                    {(canUpdate || canDelete) && <TableCell align="center"><div className={styles.actions}>
                      {canUpdate && <><IconButton size="small" color="primary" aria-label={`Editar ${record.nome}`} onClick={() => openFieldDialog(config, record)}><EditOutlined fontSize="small" /></IconButton>
                      <Switch size="small" checked={isActive(record.ativo)} onChange={() => void toggleFieldStatus(config, record)} disabled={saving} inputProps={{ "aria-label": `Alterar status de ${record.nome}` }} /></>}
                      {canDelete && <IconButton size="small" color="error" aria-label={`Excluir ${record.nome}`} onClick={() => setDeleteTarget({ type: "field", config, record })}><DeleteOutline fontSize="small" /></IconButton>}
                    </div></TableCell>}
                  </TableRow>
                );
              }) : <TableRow><TableCell className={styles.empty} colSpan={(canUpdate || canDelete) ? (config.relation ? 4 : 3) : (config.relation ? 3 : 2)}>Nenhum registro encontrado.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  const renderFieldRows = (configs: CampoConfig[], canCreate: boolean, canUpdate: boolean, canDelete: boolean) => (
    <div className={styles.sectionTable}>
      <div className={styles.tableWrap}>
        <Table size="small">
          <TableHead className={stylesTable.tableHead}>
            <TableRow>
              <TableCell className={stylesTable.tableHeadRow}>Campo</TableCell>
              <TableCell align="center" className={stylesTable.tableHeadRow}>Status</TableCell>
              <TableCell align="right" className={stylesTable.tableHeadRow}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((config) => {
              const key = `${config.table}:${config.fixedTipo || "all"}`;
              const data = records[key] || [];
              const isDetailExpanded = expandedFields[key];
              const relationRecords = config.relation ? relatedOptions[config.relation] : [];
              const detailColumnCount = config.relation ? 4 : 3;

              return (
                <Fragment key={key}>
                  <TableRow key={key} className={stylesTable.tableRow}>
                    <TableCell colSpan={2}>
                      <div className={styles.configName}>{config.title}</div>
                      <div className={styles.configDescription}>{config.subtitle}</div>
                    </TableCell>
                    <TableCell align="right" className={styles.actionsCell}>
                      <div className={styles.configActions}>
                        <Tooltip title="Visualizar registros" placement="top" arrow>
                          <IconButton size="small" aria-label={`Visualizar registros de ${config.title}`} onClick={() => setExpandedFields((current) => ({ ...current, [key]: !current[key] }))}>
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canCreate && (
                          <>
                            <span className={styles.actionSeparator} />
                            <Tooltip title="Adicionar registro" placement="top" arrow>
                              <IconButton size="small" aria-label={`Adicionar registro em ${config.title}`} onClick={() => openFieldDialog(config)}>
                                <AddOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <span className={styles.actionSeparator} />
                        <Tooltip title={isDetailExpanded ? "Ocultar registros" : "Expandir registros"} placement="top" arrow>
                          <IconButton size="small" aria-label={`Exibir registros de ${config.title}`} onClick={() => setExpandedFields((current) => ({ ...current, [key]: !current[key] }))}>
                            {isDetailExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                  {isDetailExpanded && (
                    <TableRow key={`${key}-details`} className={styles.detailRow}>
                      <TableCell colSpan={3}>
                        <div className={styles.detailPanel}>
                          <div className={styles.detailHeader}>
                            <span className={styles.fieldTitle}>Registros cadastrados</span>
                            {canCreate && (
                              <Tooltip title="Novo registro" placement="top" arrow>
                                <IconButton size="small" aria-label={`Novo registro em ${config.title}`} onClick={() => openFieldDialog(config)}>
                                  <AddOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </div>
                          <div className={styles.tableWrap}>
                            <Table size="small">
                              <TableHead className={stylesTable.tableHead}>
                                <TableRow>
                                  <TableCell className={stylesTable.tableHeadRow}>Nome</TableCell>
                                  {config.relation && <TableCell className={stylesTable.tableHeadRow}>{config.relationLabel}</TableCell>}
                                  <TableCell align="center" className={stylesTable.tableHeadRow}>Status</TableCell>
                                  {(canUpdate || canDelete) && <TableCell align="center" className={stylesTable.tableHeadRow}>Ações</TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {initialLoading || loadingFields[key] ? (
                                  Array.from({ length: 4 }).map((_, index) => (
                                    <TableRow key={`${key}-skeleton-${index}`}>
                                      <TableCell colSpan={canUpdate || canDelete ? detailColumnCount : detailColumnCount - 1}>
                                        <Skeleton animation="wave" height={26} />
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : data.length ? (
                                  data.map((record, index) => {
                                    const relationName = relationRecords.find((item) => item.id === Number(record[config.relation || ""]))?.nome || "-";

                                    return (
                                      <TableRow key={record.id} className={`${stylesTable.tableRow} ${index % 2 === 0 ? stylesTable.tableRowEven : stylesTable.tableRowOdd}`}>
                                        <TableCell className={stylesTable.textXs}>{record.nome}</TableCell>
                                        {config.relation && <TableCell className={stylesTable.textXs}>{relationName}</TableCell>}
                                        <TableCell align="center">
                                          <span className={isActive(record.ativo) ? styles.statusActive : styles.statusInactive}>
                                            {isActive(record.ativo) ? "Ativo" : "Inativo"}
                                          </span>
                                        </TableCell>
                                        {(canUpdate || canDelete) && (
                                          <TableCell align="center">
                                            <div className={styles.actions}>
                                              {canUpdate && (
                                                <>
                                                  <Tooltip title="Editar registro" placement="top" arrow>
                                                    <IconButton size="small" color="primary" aria-label={`Editar ${record.nome}`} onClick={() => openFieldDialog(config, record)}>
                                                      <EditOutlined fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title={isActive(record.ativo) ? "Inativar registro" : "Ativar registro"} placement="top" arrow>
                                                    <Switch size="small" checked={isActive(record.ativo)} onChange={() => void toggleFieldStatus(config, record)} disabled={saving} inputProps={{ "aria-label": `Alterar status de ${record.nome}` }} />
                                                  </Tooltip>
                                                </>
                                              )}
                                              {canDelete && (
                                                <Tooltip title="Excluir registro" placement="top" arrow>
                                                  <IconButton size="small" color="error" aria-label={`Excluir ${record.nome}`} onClick={() => setDeleteTarget({ type: "field", config, record })}>
                                                    <DeleteOutline fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </TableCell>
                                        )}
                                      </TableRow>
                                    );
                                  })
                                ) : (
                                  <TableRow>
                                    <TableCell className={styles.empty} colSpan={canUpdate || canDelete ? detailColumnCount : detailColumnCount - 1}>
                                      Nenhum registro encontrado.
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const renderPolosRow = () => {
    const isDetailExpanded = expandedFields.polos;

    return (
      <div className={styles.sectionTable}>
        <div className={styles.tableWrap}>
          <Table size="small">
            <TableHead className={stylesTable.tableHead}>
              <TableRow>
                <TableCell className={stylesTable.tableHeadRow}>Campo</TableCell>
                <TableCell align="center" className={stylesTable.tableHeadRow}>Status</TableCell>
                <TableCell align="right" className={stylesTable.tableHeadRow}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow className={stylesTable.tableRow}>
                <TableCell colSpan={2}>
                  <div className={styles.configName}>Polos pré-cadastrados</div>
                  <div className={styles.configDescription}>Polos utilizados para a distribuição dos municípios.</div>
                </TableCell>
                <TableCell align="right" className={styles.actionsCell}>
                  <div className={styles.configActions}>
                    <Tooltip title="Visualizar polos regionais" placement="top" arrow>
                      <IconButton size="small" aria-label="Visualizar polos regionais" onClick={() => setExpandedFields((current) => ({ ...current, polos: !current.polos }))}>
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canCreateRegionals && (
                      <>
                        <span className={styles.actionSeparator} />
                        <Tooltip title="Adicionar polo regional" placement="top" arrow>
                          <IconButton size="small" aria-label="Adicionar polo regional" onClick={() => openPoloDialog()}>
                            <AddOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <span className={styles.actionSeparator} />
                    <Tooltip title={isDetailExpanded ? "Ocultar polos regionais" : "Expandir polos regionais"} placement="top" arrow>
                      <IconButton size="small" aria-label="Exibir polos regionais" onClick={() => setExpandedFields((current) => ({ ...current, polos: !current.polos }))}>
                        {isDetailExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
              {isDetailExpanded && (
                <TableRow className={styles.detailRow}>
                  <TableCell colSpan={3}>
                    <div className={styles.detailPanel}>
                      <div className={styles.detailHeader}>
                        <span className={styles.fieldTitle}>Polos regionais cadastrados</span>
                        {canCreateRegionals && (
                          <Tooltip title="Novo polo regional" placement="top" arrow>
                            <IconButton size="small" aria-label="Novo polo regional" onClick={() => openPoloDialog()}>
                              <AddOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                      <div className={styles.tableWrap}>
                        <Table size="small">
                          <TableHead className={stylesTable.tableHead}>
                            <TableRow>
                              <TableCell className={stylesTable.tableHeadRow}>Polo regional</TableCell>
                              <TableCell className={stylesTable.tableHeadRow}>Cidade sede</TableCell>
                              <TableCell align="center" className={stylesTable.tableHeadRow}>Status</TableCell>
                              {(canUpdateRegionals || canDeleteRegionals) && <TableCell align="center" className={stylesTable.tableHeadRow}>Ações</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {initialLoading || loadingPolos ? (
                              Array.from({ length: 4 }).map((_, index) => (
                                <TableRow key={`polo-skeleton-${index}`}>
                                  <TableCell colSpan={canUpdateRegionals || canDeleteRegionals ? 4 : 3}>
                                    <Skeleton animation="wave" height={26} />
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : polos.length ? (
                              polos.map((polo, index) => (
                                <TableRow key={polo.id} className={`${stylesTable.tableRow} ${index % 2 === 0 ? stylesTable.tableRowEven : stylesTable.tableRowOdd}`}>
                                  <TableCell className={stylesTable.textXs}>{polo.nome}</TableCell>
                                  <TableCell className={stylesTable.textXs}>{polo.cidade_sede || "-"}</TableCell>
                                  <TableCell align="center">
                                    <span className={isActive(polo.ativo) ? styles.statusActive : styles.statusInactive}>
                                      {isActive(polo.ativo) ? "Ativo" : "Inativo"}
                                    </span>
                                  </TableCell>
                                  {(canUpdateRegionals || canDeleteRegionals) && (
                                    <TableCell align="center">
                                      <div className={styles.actions}>
                                        {canUpdateRegionals && (
                                          <>
                                            <Tooltip title="Editar polo regional" placement="top" arrow>
                                              <IconButton size="small" color="primary" aria-label={`Editar ${polo.nome}`} onClick={() => openPoloDialog(polo)}>
                                                <EditOutlined fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Gerenciar municípios" placement="top" arrow>
                                              <IconButton size="small" color="primary" aria-label={`Gerenciar municípios de ${polo.nome}`} onClick={() => void openMunicipiosDialog(polo)}>
                                                <LocationCityOutlined fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </>
                                        )}
                                        {canDeleteRegionals && (
                                          <Tooltip title="Excluir polo regional" placement="top" arrow>
                                            <IconButton size="small" color="error" aria-label={`Excluir ${polo.nome}`} onClick={() => setDeleteTarget({ type: "polo", polo })}>
                                              <DeleteOutline fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </div>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell className={styles.empty} colSpan={canUpdateRegionals || canDeleteRegionals ? 4 : 3}>
                                  Nenhum polo regional encontrado.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  const renderRelationships = () => {
    if (!canViewUsers) return null;

    const tableLabel = getProfessionalTableLabel;

    return (
      <div className={styles.relationshipsCard}>
        <div className={styles.fieldHeader}>
          <div>
            <div className={styles.fieldTitle}>Relacionamentos entre campos</div>
            <div className={styles.fieldDescription}>Gerencie versões e regras de exibição dos dados profissionais.</div>
          </div>
          <div className={styles.relationshipVersionActions}>
            {selectedRelationshipVersion && (
              <Button
                className={stylesButton.buttonSoftOutline}
                startIcon={<VisibilityOutlined />}
                onClick={() => setRelationshipPreviewOpen(true)}
              >
                Pré-visualizar formulário
              </Button>
            )}
            {canCreateUsers && <Button className={stylesButton.buttonSmallSoftPrimary} startIcon={<AddOutlined />} onClick={() => openVersionDialog()}>Nova versão</Button>}
            {canCreateUsers && isRelationshipDraft && <Button className={stylesButton.buttonSmallSoftPrimary} startIcon={<AddOutlined />} onClick={() => openRelationshipDialog()}>Nova regra</Button>}
          </div>
        </div>
        <div className={styles.relationshipVersionBar}>
          <SearchSelect
            label=""
            placeholder="Selecione uma versão"
            options={relationshipVersions.map((version) => ({ id: version.id, nome: `Versão ${version.numero}${version.nome ? ` - ${version.nome}` : ""} (${version.status})` }))}
            value={selectedRelationshipVersionId}
            onChange={(versionId) => {
              setSelectedRelationshipVersionId(versionId);
              void loadRelationships(versionId);
            }}
            disabled={loadingRelationshipVersions}
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            labelClassname={stylesInput.labelSelect}
            inputClassname={stylesInput.inputSoft}
          />
          {selectedRelationshipVersion && (
            <div className={styles.relationshipVersionControls}>
              <span className={selectedRelationshipVersion.status === "RASCUNHO" ? styles.statusInactive : styles.statusActive}>{selectedRelationshipVersion.status}</span>
              {canUpdateUsers && isRelationshipDraft && <Tooltip title="Renomear versão" arrow><IconButton size="small" color="primary" onClick={() => openVersionDialog(selectedRelationshipVersion)}><DriveFileRenameOutline fontSize="small" /></IconButton></Tooltip>}
              {canUpdateUsers && isRelationshipDraft && <Tooltip title="Publicar versão" arrow><IconButton size="small" color="primary" onClick={() => void publishRelationshipVersion()}><PublishOutlined fontSize="small" /></IconButton></Tooltip>}
              {canDeleteUsers && isRelationshipDraft && <Tooltip title="Excluir versão" arrow><IconButton size="small" color="error" onClick={() => setDeleteTarget({ type: "relationshipVersion", version: selectedRelationshipVersion })}><DeleteOutline fontSize="small" /></IconButton></Tooltip>}
            </div>
          )}
        </div>
        <div className={styles.tableWrap}>
          <Table size="small" className={styles.relationshipsTable}>
            <TableHead>
              <TableRow>
                <TableCell>Campo de origem</TableCell>
                <TableCell>Valor de origem</TableCell>
                <TableCell>Campo de destino</TableCell>
                <TableCell>Tabela de opções</TableCell>
                <TableCell align="center">Ordem</TableCell>
                <TableCell align="center">Status</TableCell>
                {isRelationshipDraft && (canUpdateUsers || canDeleteUsers) && <TableCell align="center">Ações</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingRelationships ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <TableRow key={`relationship-skeleton-${index}`}>
                    <TableCell colSpan={isRelationshipDraft && (canUpdateUsers || canDeleteUsers) ? 7 : 6}><Skeleton animation="wave" height={28} /></TableCell>
                  </TableRow>
                ))
              ) : relationships.length ? (
                relationships.map((relationship) => {
                  const sourceTable = SOURCE_FIELD_TABLE_MAP[relationship.source_field];
                  const sourceValue = relationship.source_value_id === null || isActive(relationship.aplicar_para_todos)
                    ? "Todos"
                    : relationship.source_field === "municipio_id"
                      ? cities.find((city) => city.id === relationship.source_value_id)?.nome
                    : sourceTable
                    ? records[sourceTable]?.find((record) => record.id === relationship.source_value_id)?.nome
                    : String(relationship.source_value_id);

                  return (
                    <TableRow key={relationship.id}>
                      <TableCell>{fieldToLabel(relationship.source_field)}</TableCell>
                      <TableCell>{sourceValue || relationship.source_value_id}</TableCell>
                      <TableCell>{relationship.target_label || fieldToLabel(relationship.target_field)}</TableCell>
                      <TableCell>{tableLabel(relationship.options_table)}</TableCell>
                      <TableCell align="center">{relationship.ordem ?? "-"}</TableCell>
                      <TableCell align="center">
                        <span className={isActive(relationship.ativo) ? styles.statusActive : styles.statusInactive}>
                          {isActive(relationship.ativo) ? "Ativo" : "Inativo"}
                        </span>
                      </TableCell>
                      {isRelationshipDraft && (canUpdateUsers || canDeleteUsers) && (
                        <TableCell align="center">
                          <div className={styles.actions}>
                            {canUpdateUsers && isRelationshipDraft && (
                              <Tooltip title="Editar regra" placement="top" arrow>
                                <IconButton size="small" color="primary" aria-label={`Editar regra ${relationship.id}`} onClick={() => openRelationshipDialog(relationship)}>
                                  <EditOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDeleteUsers && isRelationshipDraft && (
                              <Tooltip title="Excluir regra" placement="top" arrow>
                                <IconButton size="small" color="error" aria-label={`Excluir regra ${relationship.id}`} onClick={() => setDeleteTarget({ type: "relationship", relationship })}>
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell className={styles.empty} colSpan={isRelationshipDraft && (canUpdateUsers || canDeleteUsers) ? 7 : 6}>Nenhuma regra de relacionamento cadastrada.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  const renderSection = (key: string, title: string, description: string, icon: JSX.Element, visible: boolean, content: JSX.Element) => {
    if (!visible) return null;
    const isExpanded = expanded[key];
    return <section className={styles.section}>
      <button type="button" className={styles.sectionHeader} onClick={() => setExpanded((current) => ({ ...current, [key]: !current[key] }))} aria-expanded={isExpanded}>
        <span className={styles.sectionTitleWrap}><span className={styles.sectionIcon}>{icon}</span><span><span className={styles.sectionTitle}>{title}</span><span className={styles.sectionDescription}>{description}</span></span></span>
        {isExpanded ? <ExpandLess color="primary" /> : <ExpandMore color="primary" />}
      </button>
      {isExpanded && <div className={styles.sectionContent}>{key === "regionals" ? renderPolosRow() : content}</div>}
    </section>;
  };

  const renderPageSkeleton = () => (
    <main className={styles.page} aria-busy="true" aria-label="Carregando painéis administrativos">
      <div className={styles.pageSkeleton}>
        <div>
          <Skeleton animation="wave" width={340} height={42} />
          <Skeleton animation="wave" width={540} height={24} />
        </div>
        {[5, 2, 1].map((rowCount, sectionIndex) => (
          <section key={`page-skeleton-section-${sectionIndex}`} className={styles.skeletonSection}>
            <div className={styles.skeletonSectionHeader}>
              <Skeleton animation="wave" variant="rounded" width={40} height={40} />
              <div>
                <Skeleton animation="wave" width={240} height={24} />
                <Skeleton animation="wave" width={360} height={18} />
              </div>
            </div>
            <div className={styles.skeletonSectionContent}>
              {Array.from({ length: rowCount }).map((_, rowIndex) => (
                <div key={`page-skeleton-row-${sectionIndex}-${rowIndex}`} className={styles.skeletonTableRow}>
                  <Skeleton animation="wave" height={24} />
                  <Skeleton animation="wave" height={24} />
                  <Skeleton animation="wave" height={24} />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </main>
  );

  const deleteTargetLabel = () => {
    if (!deleteTarget) return "o registro selecionado";
    if (deleteTarget.type === "polo") return `o polo regional ${deleteTarget.polo.nome}`;
    if (deleteTarget.type === "relationship") return "a regra de relacionamento selecionada";
    if (deleteTarget.type === "relationshipVersion") return `a versão ${deleteTarget.version.numero}`;
    return `o registro ${deleteTarget.record.nome}`;
  };

  if (initialLoading) {
    return renderPageSkeleton();
  }

  return (
    <main className={styles.page}>
      <h1 className={styles.title}>Painéis Administrativos</h1>
      <p className={styles.subtitle}>Gerencie os campos e opções do sistema conforme a necessidade da sua instituição.</p>
      {message && <Alert className={stylesTable.message} severity={message.type} onClose={() => setMessage(null)}>{message.text}</Alert>}

      {renderSection("users", "Campos do Cadastro de Usuário", "Gerencie os dados complementares usados no cadastro de usuários.", <PersonOutline />, canViewUsers, renderFieldRows(USER_FIELDS, canCreateUsers, canUpdateUsers, canDeleteUsers))}
      {renderSection("actions", "Campos da Gestão de Ações de Qualificação", "Gerencie os campos das ações de qualificação.", <SchoolOutlined />, canViewActions, renderFieldRows(ACTION_FIELDS, canCreateActions, canUpdateActions, canDeleteActions))}
      {renderSection("regionals", "Gestão de Polos Regionais", "Gerencie os polos regionais e seus municípios vinculados.", <GroupsOutlined />, canViewRegionals, <div className={styles.fieldCard}>
        <div className={styles.fieldHeader}><div><div className={styles.fieldTitle}>Polos regionais</div><div className={styles.fieldDescription}>Polos utilizados para a distribuição de municípios.</div></div>{canCreateRegionals && <Button className={stylesButton.buttonSmallSoftPrimary} startIcon={<AddOutlined />} onClick={() => openPoloDialog()}>Novo polo</Button>}</div>
        <div className={styles.tableWrap}><Table size="small"><TableHead className={stylesTable.tableHead}><TableRow><TableCell className={stylesTable.tableHeadRow}>Polo regional</TableCell><TableCell className={stylesTable.tableHeadRow}>Cidade sede</TableCell><TableCell className={stylesTable.tableHeadRow} align="center">Status</TableCell>{(canUpdateRegionals || canDeleteRegionals) && <TableCell className={stylesTable.tableHeadRow} align="center">Ações</TableCell>}</TableRow></TableHead><TableBody>{initialLoading || loadingPolos ? Array.from({ length: 4 }).map((_, index) => <TableRow key={`polo-skeleton-${index}`}><TableCell colSpan={(canUpdateRegionals || canDeleteRegionals) ? 4 : 3}><Skeleton animation="wave" height={26} /></TableCell></TableRow>) : polos.length ? polos.map((polo, index) => <TableRow key={polo.id} className={`${stylesTable.tableRow} ${index % 2 === 0 ? stylesTable.tableRowEven : stylesTable.tableRowOdd}`}><TableCell className={stylesTable.textXs}>{polo.nome}</TableCell><TableCell className={stylesTable.textXs}>{polo.cidade_sede || "-"}</TableCell><TableCell align="center"><span className={isActive(polo.ativo) ? styles.statusActive : styles.statusInactive}>{isActive(polo.ativo) ? "Ativo" : "Inativo"}</span></TableCell>{(canUpdateRegionals || canDeleteRegionals) && <TableCell align="center"><div className={styles.actions}>{canUpdateRegionals && <><IconButton size="small" color="primary" aria-label={`Editar ${polo.nome}`} onClick={() => openPoloDialog(polo)}><EditOutlined fontSize="small" /></IconButton><IconButton size="small" color="primary" aria-label={`Gerenciar municípios de ${polo.nome}`} onClick={() => openMunicipiosDialog(polo)}><LocationCityOutlined fontSize="small" /></IconButton></>}{canDeleteRegionals && <IconButton size="small" color="error" aria-label={`Excluir ${polo.nome}`} onClick={() => setDeleteTarget({ type: "polo", polo })}><DeleteOutline fontSize="small" /></IconButton>}</div></TableCell>}</TableRow>) : <TableRow><TableCell className={styles.empty} colSpan={(canUpdateRegionals || canDeleteRegionals) ? 4 : 3}>Nenhum polo regional encontrado.</TableCell></TableRow>}</TableBody></Table></div>
      </div>)}
      {renderSection(
        "relationships",
        "Relacionamentos entre Campos",
        "Defina regras de exibição para os campos dos dados profissionais.",
        <AccountTreeOutlined />,
        canViewUsers,
        <>{renderRelationships()}</>,
      )}

      <Dialog open={fieldDialogOpen} onClose={() => !saving && setFieldDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ className: styles.dialogPaper }}>
        <DialogTitle className={styles.dialogTitle}>
          <div className={styles.dialogTitleText}>{fieldForm.id ? "Editar" : "Cadastrar"} {fieldConfig?.title}</div>
          <div className={styles.dialogSubtitle}>Preencha as informações para disponibilizar esta opção no sistema.</div>
        </DialogTitle>
        <DialogContent className={`${stylesGeneral.mt4} ${stylesGrid.grid12}`}>

          <div className={`${styles.filterField} ${stylesGrid.inputGroupFull}`}>
            <label>Nome</label>
            <TextField
              variant="standard"
              fullWidth
              className={styles.fullColumn}
              size="small"
              label=""
              value={fieldForm.nome}
              onChange={(event) => setFieldForm((current) => ({ ...current, nome: event.target.value }))}
              InputProps={{ className: `${stylesInput.inputSoft}`, disableUnderline: true }}
            />
          </div>  
          
          {fieldConfig?.description && (
            <div className={`${styles.filterField} ${stylesGrid.inputGroupFull}`}>
              <label>Descrição</label>
              <TextField
                className={styles.fullColumn}
                size="small"
                label=""
                variant="standard"
                fullWidth
                multiline
                minRows={3}
                value={fieldForm.descricao}
                onChange={(event) => setFieldForm((current) => ({ ...current, descricao: event.target.value }))}
                InputProps={{ className: `${stylesInput.inputSoft}`, disableUnderline: true }}
              />
            </div>
          )}

          {fieldConfig?.relation && (
            <div className={`${styles.filterField} ${stylesGrid.inputGroupFull}`}>
              <label>Relacionamento - {fieldConfig.relationLabel}</label>
              <SearchSelect
                label=""
                placeholder=""
                options={relatedOptions[fieldConfig.relation]}
                value={Number(fieldForm.relationId) || null}
                onChange={(value) => setFieldForm((current) => ({ ...current, relationId: value ? String(value) : "" }))}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={`${stylesInput.inputSoft}`}
              />
            </div>
          )}
          <div className={`${styles.filterField} ${stylesGrid.inputGroupFull}`}>
            <FormControlLabel
              control={<Switch checked={fieldForm.ativo} onChange={(event) => setFieldForm((current) => ({ ...current, ativo: event.target.checked }))} />}
              label="Ativo"
            />
          </div>

        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button className={stylesButton.buttonSoftMuted} onClick={() => setFieldDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button className={stylesButton.buttonSoftPrimary} onClick={() => void saveField()} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={poloDialogOpen} onClose={() => !saving && setPoloDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ className: styles.dialogPaper }}>
        <DialogTitle className={styles.dialogTitle}>
          <div className={styles.dialogTitleText}>{poloForm.id ? "Editar polo regional" : "Cadastrar polo regional"}</div>
          <div className={styles.dialogSubtitle}>Informe os dados do polo regional que será usado na distribuição de vagas.</div>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <div className={`${stylesGeneral.mt4} ${stylesGrid.grid12}`}>
            
            <div className={`${styles.filterField} ${stylesGrid.inputGroupFull}`}>
              <label>Nome</label>
              <TextField 
                variant="standard"
                fullWidth
                size="small" 
                label="" 
                value={poloForm.nome} 
                onChange={(event) => setPoloForm((current) => ({ ...current, nome: event.target.value }))} 
                InputProps={{ className: `${stylesInput.inputSoft}`, disableUnderline: true }}
              />
            </div>

            <div className={`${styles.filterField} ${stylesGrid.inputGroupFull}`}>
              <label>Cidade Sede</label>
              <TextField 
                variant="standard"
                fullWidth
                size="small" 
                label="" 
                value={poloForm.cidade_sede} 
                onChange={(event) => setPoloForm((current) => ({ ...current, cidade_sede: event.target.value }))} 
                InputProps={{ className: `${stylesInput.inputSoft}`, disableUnderline: true }}
              />
            </div>

            <div className={`${styles.filterField} ${stylesGrid.inputGroupFull}`}>
              <label>Motivo da alteração</label>
              <TextField 
                variant="standard"
                fullWidth
                size="small" 
                className={styles.fullColumn} 
                label="" 
                value={poloForm.motivo_alteracao} 
                onChange={(event) => setPoloForm((current) => ({ ...current, motivo_alteracao: event.target.value }))} 
                InputProps={{ className: `${stylesInput.inputSoft}`, disableUnderline: true }} 
              />
            </div>
            
            <div className={`${styles.filterField} ${stylesGrid.inputGroupFull}`}>
              <FormControlLabel 
                control={<Switch checked={poloForm.ativo} 
                onChange={(event) => setPoloForm((current) => ({ ...current, ativo: event.target.checked }))} />} 
                label="Ativo" 
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button className={stylesButton.buttonSoftMuted} onClick={() => setPoloDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button className={stylesButton.buttonSoftPrimary} onClick={() => void savePolo()} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(municipiosPolo)} onClose={() => !saving && setMunicipiosPolo(null)} fullWidth maxWidth="lg" PaperProps={{ className: styles.dialogPaper }}>
        <DialogTitle className={styles.dialogTitle}>
          <div className={styles.dialogTitleText}>Municípios do polo {municipiosPolo?.nome}</div>
          <div className={styles.dialogSubtitle}>Selecione os municípios vinculados a este polo regional.</div>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {loadingMunicipiosPolo ? (
            <div className={styles.municipiosLoading}>
              <Skeleton animation="wave" height={30} width="35%" />
              <Skeleton animation="wave" variant="rounded" height={280} />
            </div>
          ) : (
            <MultiSelectTransferField
              label="Municípios"
              placeholder="Pesquisar município"
              value={selectedCityIds}
              onChange={(value) => setSelectedCityIds(value.map(Number).filter(Number.isFinite))}
              options={cities}
            />
          )}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button className={stylesButton.buttonSoftMuted} onClick={() => setMunicipiosPolo(null)} disabled={saving || loadingMunicipiosPolo}>Cancelar</Button>
          <Button className={stylesButton.buttonSoftPrimary} startIcon={<CheckCircleOutline />} onClick={() => void saveMunicipios()} disabled={saving || loadingMunicipiosPolo}>{saving ? "Salvando..." : "Salvar municípios"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={versionDialogOpen} onClose={() => !saving && setVersionDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ className: styles.dialogPaper }}>
        <DialogTitle className={styles.dialogTitle}>
          <div className={styles.dialogTitleText}>{editingVersion ? "Renomear versão" : "Criar versão de relacionamentos"}</div>
          <div className={styles.dialogSubtitle}>{editingVersion ? "Somente versões em rascunho podem ser alteradas." : "A nova versão será criada como rascunho."}</div>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <div className={`${styles.filterField} ${stylesGrid.inputGroupFull}`}>
            <label>Nome da versão</label>
            <TextField
              variant="standard"
              fullWidth
              size="small"
              value={versionName}
              onChange={(event) => setVersionName(event.target.value)}
              InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
            />
          </div>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button className={stylesButton.buttonSoftMuted} onClick={() => setVersionDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button className={stylesButton.buttonSoftPrimary} onClick={() => void saveRelationshipVersion()} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={relationshipPreviewOpen}
        onClose={() => setRelationshipPreviewOpen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{ className: `${styles.dialogPaper} ${styles.previewDialogPaper}` }}
      >
        <DialogTitle className={styles.dialogTitle}>
          <div className={styles.dialogTitleText}>Pré-visualização dos dados profissionais</div>
          <div className={styles.dialogSubtitle}>
            {selectedRelationshipVersion
              ? `Versão ${selectedRelationshipVersion.numero}${selectedRelationshipVersion.nome ? ` - ${selectedRelationshipVersion.nome}` : ""} (${selectedRelationshipVersion.status})`
              : "Selecione uma versão para pré-visualizar o formulário."}
          </div>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {selectedRelationshipVersion ? (
            <RelationshipFormPreview
              key={selectedRelationshipVersion.id}
              rules={relationships}
              records={records}
              cities={cities}
              areasAtuacao={areasAtuacao}
            />
          ) : null}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button className={stylesButton.buttonSoftMuted} onClick={() => setRelationshipPreviewOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={relationshipDialogOpen} onClose={() => !saving && setRelationshipDialogOpen(false)} fullWidth maxWidth="md" PaperProps={{ className: styles.dialogPaper }}>
        <DialogTitle className={styles.dialogTitle}>
          <div className={styles.dialogTitleText}>{relationshipForm.id ? "Editar relacionamento" : "Cadastrar relacionamento"}</div>
          <div className={styles.dialogSubtitle}>Configure regras do formulário de dados profissionais.</div>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
           <div className={`${stylesGeneral.mt4} ${stylesGrid.grid12}`}>
              <div className={`${styles.filterField} ${stylesGrid.inputGroup4}`}>
                <label>Campo de origem *</label>
                <SearchSelect
                  label=""
                  placeholder="Selecione"
                  options={toSearchSelectOptions(PROFESSIONAL_FIELD_OPTIONS)}
                  value={getSearchSelectValue(toSearchSelectOptions(PROFESSIONAL_FIELD_OPTIONS), relationshipForm.sourceField)}
                  onChange={(selectedId) => {
                    const options = toSearchSelectOptions(PROFESSIONAL_FIELD_OPTIONS);
                    const sourceField = options.find((option) => option.id === selectedId)?.value || "";
                    handleRelationshipSourceFieldChange(sourceField);
                  }}
                  paperClassname={stylesInput.paper}
                  popperClassname={stylesInput.popper}
                  labelClassname={stylesInput.labelSelect}
                  inputClassname={`${stylesInput.inputSoft} ${styles.dialogInput}`}
                />
              </div>
              
              <div className={`${styles.filterField} ${stylesGrid.inputGroup4} ${stylesGrid.itemsEnd} ${stylesGrid.justifyCenter}`}>
                <FormControlLabel
                  className={styles.dialogToggle}
                  control={
                    <Switch
                      checked={relationshipForm.applyToAll}
                      onChange={(event) => setRelationshipForm((current) => ({
                        ...current,
                        applyToAll: event.target.checked,
                        sourceValueId: event.target.checked ? "" : current.sourceValueId,
                      }))}
                    />
                  }
                  label="Aplicar para todos"
                />
              </div>

            {!relationshipForm.applyToAll && sourceFieldConfig && (
              <div className={`${styles.filterField} ${stylesGrid.inputGroup4}`}>
                <label>Valor de origem *</label>
                <SearchSelect
                  label=""
                  placeholder={sourceValuesLoading ? "Carregando..." : "Selecione"}
                  options={sourceValues}
                  value={Number(relationshipForm.sourceValueId) || null}
                  onChange={(value) => setRelationshipForm((current) => ({ ...current, sourceValueId: value ? String(value) : "" }))}
                  disabled={sourceValuesLoading}
                  disableInactiveOptions
                  paperClassname={stylesInput.paper}
                  popperClassname={stylesInput.popper}
                  labelClassname={stylesInput.labelSelect}
                  inputClassname={`${stylesInput.inputSoft} ${styles.dialogInput}`}
                />
              </div>
            )}

            <div className={`${styles.filterField} ${stylesGrid.inputGroup4}`}>
              <label>Campo de destino *</label>
              <SearchSelect
                label=""
                placeholder="Selecione"
                options={toSearchSelectOptions(PROFESSIONAL_FIELD_OPTIONS)}
                value={getSearchSelectValue(toSearchSelectOptions(PROFESSIONAL_FIELD_OPTIONS), relationshipForm.targetField)}
                onChange={(selectedId) => {
                  const options = toSearchSelectOptions(PROFESSIONAL_FIELD_OPTIONS);
                  const targetField = options.find((option) => option.id === selectedId)?.value || "";
                  const targetLabel = PROFESSIONAL_FIELD_OPTIONS.find((field) => field.value === targetField)?.label || "";
                  setRelationshipForm((current) => ({ ...current, targetField, targetLabel: current.targetLabel || targetLabel }));
                }}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={`${stylesInput.inputSoft}`}
              />
            </div>

            <div className={`${styles.filterField} ${stylesGrid.inputGroup4}`}>
              <label>Rótulo do campo de destino *</label>
              <TextField
                variant="standard"
                size="small"
                label=""
                value={relationshipForm.targetLabel}
                onChange={(event) => setRelationshipForm((current) => ({ ...current, targetLabel: event.target.value }))}
                InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
              />
            </div>
            
            <div className={`${styles.filterField} ${stylesGrid.inputGroup4}`}>
              <label>Tabela de opções *</label>
              <SearchSelect
                label=""
                placeholder="Selecione"
                options={toSearchSelectOptions(PROFESSIONAL_TABLES.map((table) => ({ value: table, label: getProfessionalTableLabel(table) })))}
                value={getSearchSelectValue(toSearchSelectOptions(PROFESSIONAL_TABLES.map((table) => ({ value: table, label: getProfessionalTableLabel(table) }))), relationshipForm.optionsTable)}
                onChange={(selectedId) => {
                  const options = toSearchSelectOptions(PROFESSIONAL_TABLES.map((table) => ({ value: table, label: getProfessionalTableLabel(table) })));
                  const optionsTable = options.find((option) => option.id === selectedId)?.value || "";
                  setRelationshipForm((current) => ({ ...current, optionsTable, optionsFilters: [] }));
                }}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={`${stylesInput.inputSoft}`}
              />
            </div>

            <div className={`${styles.relationshipFilters} ${stylesGrid.inputGroupFull}`}>
              <div className={styles.relationshipFiltersHeader}>
                <span>Filtros das opções</span>
                <Button className={stylesButton.buttonSmallSoftMuted} onClick={() => setRelationshipForm((current) => ({ ...current, optionsFilters: [...current.optionsFilters, { field: "", operator: "=", value: "" }] }))}>
                  Adicionar filtro
                </Button>
              </div>
              {relationshipForm.optionsFilters.map((filter, index) => (
                <div key={`relationship-filter-${index}`} className={styles.relationshipFilterRow}>
                  <div className={`${styles.filterField}`}>
                    <label>Coluna</label>
                    <SearchSelect
                      label=""
                      placeholder="Selecione"
                      options={toSearchSelectOptions((tableColumns[relationshipForm.optionsTable] || []).map((column) => ({ value: column, label: column })))}
                      value={getSearchSelectValue(toSearchSelectOptions((tableColumns[relationshipForm.optionsTable] || []).map((column) => ({ value: column, label: column }))), filter.field)}
                      onChange={(selectedId) => {
                        const options = toSearchSelectOptions((tableColumns[relationshipForm.optionsTable] || []).map((column) => ({ value: column, label: column })));
                        const field = options.find((option) => option.id === selectedId)?.value || "";
                        setRelationshipForm((current) => ({ ...current, optionsFilters: current.optionsFilters.map((currentFilter, filterIndex) => filterIndex === index ? { ...currentFilter, field } : currentFilter) }));
                      }}
                      paperClassname={stylesInput.paper}
                      popperClassname={stylesInput.popper}
                      labelClassname={stylesInput.labelSelect}
                      inputClassname={`${stylesInput.inputSoft}`}
                    />
                  </div>
                  
                  <div className={`${styles.filterField}`}>
                    <label>Operador</label>
                    <SearchSelect
                      label=""
                      placeholder="Selecione"
                      options={toSearchSelectOptions([{ value: "=", label: "=" }, { value: "!=", label: "!=" }])}
                      value={getSearchSelectValue(toSearchSelectOptions([{ value: "=", label: "=" }, { value: "!=", label: "!=" }]), filter.operator)}
                      onChange={(selectedId) => {
                        const options = toSearchSelectOptions([{ value: "=", label: "=" }, { value: "!=", label: "!=" }]);
                        const operator = options.find((option) => option.id === selectedId)?.value || "=";
                        setRelationshipForm((current) => ({ ...current, optionsFilters: current.optionsFilters.map((currentFilter, filterIndex) => filterIndex === index ? { ...currentFilter, operator } : currentFilter) }));
                      }}
                      paperClassname={stylesInput.paper}
                      popperClassname={stylesInput.popper}
                      labelClassname={stylesInput.labelSelect}
                      inputClassname={`${stylesInput.inputSoft}`}
                    />
                  </div>
                  
                  <div className={`${styles.filterField}`}>
                    <label>Operador</label>
                    <TextField
                      variant="standard"
                      size="small"
                      label=""
                      value={String(filter.value)}
                      onChange={(event) => setRelationshipForm((current) => ({ ...current, optionsFilters: current.optionsFilters.map((currentFilter, filterIndex) => filterIndex === index ? { ...currentFilter, value: event.target.value } : currentFilter) }))}
                      InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
                    />
                  </div>

                  <div className={`${stylesGeneral.mt4} ${stylesGrid.itemsCenter} ${stylesGrid.justifyCenter}`}>
                  <Tooltip title="Remover filtro" placement="top" arrow>
                    <IconButton size="small" color="error" aria-label="Remover filtro" onClick={() => setRelationshipForm((current) => ({ ...current, optionsFilters: current.optionsFilters.filter((_, filterIndex) => filterIndex !== index) }))}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  </div>

                </div>
              ))}
            </div>

            <div className={`${styles.filterField} ${stylesGrid.inputGroup2} ${stylesGrid.itemsEnd} ${stylesGrid.justifyCenter}`}>
              <FormControlLabel 
                className={styles.dialogToggle} 
                control={<Switch checked={relationshipForm.active} 
                onChange={(event) => setRelationshipForm((current) => ({ ...current, active: event.target.checked }))} />} 
                label="Ativo" 
              />
            </div>

            <div className={`${styles.filterField} ${stylesGrid.inputGroup4} ${stylesGrid.itemsEnd} ${stylesGrid.justifyCenter}`}>
              <FormControlLabel 
                className={styles.dialogToggle} 
                control={<Switch checked={relationshipForm.required} 
                onChange={(event) => setRelationshipForm((current) => ({ ...current, required: event.target.checked }))} />} 
                label="Campo obrigatório" 
              />
            </div>

            <div className={`${styles.filterField} ${stylesGrid.inputGroup2}`}>
              <label>Ordem</label>
              <TextField
                variant="standard"
                size="small"
                type="number"
                label=""
                value={relationshipForm.order}
                onChange={(event) => setRelationshipForm((current) => ({ ...current, order: event.target.value }))}
                InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
              />
            </div>

          </div>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button className={stylesButton.buttonSoftMuted} onClick={() => setRelationshipDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button className={stylesButton.buttonSoftPrimary} onClick={() => void saveRelationship()} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        loading={deleting}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={() => void handleDelete()}
        title="Excluir registro"
        message={`Deseja realmente excluir ${deleteTargetLabel()}? Esta ação não poderá ser desfeita.`}
        titleButtonConfirm="Excluir"
        titleLoadButton="Excluindo..."
      />
    </main>
  );
};

export default ParametrizacaoCampos;
